import React from 'react';
import { RendererSharedProps } from '../../types/elementRenderer';

interface ArrowRendererProps extends RendererSharedProps {
  /** Called when hit area / line is mouse-downed (pre-wired handleArrowMouseDown from parent) */
  onMouseDown: (e: React.MouseEvent) => void;
  /** For selecting the element when a path-point circle or endpoint handle is grabbed */
  onSelect: (id: string) => void;
  /** For moving arrow endpoints */
  onArrowEdit?: (id: string, isStart: boolean, x: number, y: number) => void;
  /** For dragging intermediate path-point circles */
  onArrowPathPointDrag?: (id: string, idx: number, x: number, y: number) => void;
  /** For inserting a new bend point at a midpoint between two existing points */
  onArrowAddPoint?: (id: string, x: number, y: number, after?: number) => void;
}

const getMouseWorldFromSvg = (evt: MouseEvent, svgEl: SVGSVGElement, zoom: number) => {
  const rect = svgEl.getBoundingClientRect();
  return { x: (evt.clientX - rect.left) / zoom, y: (evt.clientY - rect.top) / zoom };
};

const getOffset = (type: string | undefined, markerSize: number) => {
  if (!type || type === 'none') return 0;
  if (type === 'arrow') return 8 * markerSize;
  if (type === 'diamond') return 9 * markerSize;
  if (type === 'circle') return 5 * markerSize;
  if (type === 'erd-inheritance') return 10 * markerSize;
  if (type === 'erd-association') return 7 * markerSize;
  if (type === 'erd-composition') return 10 * markerSize;
  if (type === 'erd-aggregation') return 10 * markerSize;
  return 0;
};

const getShortenedPath = (
  points: Array<{ x: number; y: number }>,
  startCap: string | undefined,
  endCap: string | undefined,
  strokeWidth: number | undefined,
) => {
  if (points.length < 2) return points;
  const markerSize = strokeWidth || 2;
  const startOffset = getOffset(startCap, markerSize);
  const endOffset = getOffset(endCap, markerSize);
  const newPoints = JSON.parse(JSON.stringify(points)) as Array<{ x: number; y: number }>;

  if (startOffset > 0) {
    const p0 = newPoints[0];
    const p1 = newPoints[1];
    const dist = Math.hypot(p1.x - p0.x, p1.y - p0.y);
    if (dist > startOffset + 10) {
      const t = startOffset / dist;
      newPoints[0] = { x: p0.x + (p1.x - p0.x) * t, y: p0.y + (p1.y - p0.y) * t };
    }
  }

  if (endOffset > 0) {
    const n = newPoints.length;
    const pn = newPoints[n - 1];
    const pn1 = newPoints[n - 2];
    const dist = Math.hypot(pn.x - pn1.x, pn.y - pn1.y);
    if (dist > endOffset + 10) {
      const t = endOffset / dist;
      newPoints[n - 1] = { x: pn.x + (pn1.x - pn.x) * t, y: pn.y + (pn1.y - pn.y) * t };
    }
  }

  return newPoints;
};

const toPathStr = (points: Array<{ x: number; y: number }>) =>
  points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

/** Renders an ARROW element: markers, hit area, shortened visible line, path-point handles. */
export const ArrowRenderer: React.FC<ArrowRendererProps> = ({
  element,
  zoom,
  filterStyle,
  onMouseDown,
  onSelect,
  onArrowEdit,
  onArrowPathPointDrag,
  onArrowAddPoint,
  onContextMenu,
}) => {
  const pts: Array<{ x: number; y: number }> =
    element.pathPoints && element.pathPoints.length >= 2
      ? (element.pathPoints as Array<{ x: number; y: number }>)
      : [
          { x: element.x, y: element.y },
          { x: element.x2 ?? element.x, y: element.y2 ?? element.y },
        ];

  const pathStr = toPathStr(pts);
  const shortenedPts = getShortenedPath(pts, element.startCap as string | undefined, element.endCap as string | undefined, element.strokeWidth);
  const visiblePathStr = toPathStr(shortenedPts);

  const markerPre = `marker-${element.id}`;
  const col = element.stroke || '#000';

  const getStartMarker = (cap: unknown) => {
    if (cap === 'arrow') return `url(#${markerPre}-arrowhead-start)`;
    if (cap === 'diamond') return `url(#${markerPre}-diamond-start)`;
    if (cap === 'circle') return `url(#${markerPre}-circle-start)`;
    if (cap === 'erd-inheritance') return `url(#${markerPre}-erd-inherit-start)`;
    if (cap === 'erd-association') return `url(#${markerPre}-erd-assoc-start)`;
    if (cap === 'erd-composition') return `url(#${markerPre}-erd-comp-start)`;
    if (cap === 'erd-aggregation') return `url(#${markerPre}-erd-agg-start)`;
    return 'none';
  };
  const getEndMarker = (cap: unknown) => {
    if (cap === 'arrow') return `url(#${markerPre}-arrowhead-end)`;
    if (cap === 'diamond') return `url(#${markerPre}-diamond-end)`;
    if (cap === 'circle') return `url(#${markerPre}-circle-end)`;
    if (cap === 'erd-inheritance') return `url(#${markerPre}-erd-inherit-end)`;
    if (cap === 'erd-association') return `url(#${markerPre}-erd-assoc-end)`;
    if (cap === 'erd-composition') return `url(#${markerPre}-erd-comp-end)`;
    if (cap === 'erd-aggregation') return `url(#${markerPre}-erd-agg-end)`;
    return 'none';
  };

  const z = zoom || 1;

  const handleArrowPointMouseDown = (e: React.MouseEvent, isStart: boolean) => {
    e.stopPropagation();
    onSelect(element.id);
    const svgEl = (e.target as SVGElement).closest('svg') as SVGSVGElement | null;
    if (!svgEl) return;
    const move = (ev: MouseEvent) => {
      const { x, y } = getMouseWorldFromSvg(ev, svgEl, z);
      onArrowEdit?.(element.id, isStart, x, y);
    };
    const up = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  };

  return (
    <g>
      <defs>
        {/* END markers */}
        <marker id={`${markerPre}-arrowhead-end`} markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
          <path d="M0,0 L0,6 L9,3 z" fill={col} />
        </marker>
        <marker id={`${markerPre}-diamond-end`} markerWidth="12" markerHeight="12" refX="10" refY="5" orient="auto" markerUnits="strokeWidth">
          <path d="M0,5 L5,0 L10,5 L5,10 z" fill={col} />
        </marker>
        <marker id={`${markerPre}-circle-end`} markerWidth="10" markerHeight="10" refX="6" refY="3" orient="auto" markerUnits="strokeWidth">
          <circle cx="3" cy="3" r="3" fill={col} />
        </marker>
        {/* START markers */}
        <marker id={`${markerPre}-arrowhead-start`} markerWidth="10" markerHeight="10" refX="0" refY="3" orient="auto" markerUnits="strokeWidth">
          <path d="M10,0 L10,6 L0,3 z" fill={col} />
        </marker>
        <marker id={`${markerPre}-diamond-start`} markerWidth="12" markerHeight="12" refX="0" refY="5" orient="auto" markerUnits="strokeWidth">
          <path d="M0,5 L5,0 L10,5 L5,10 z" fill={col} />
        </marker>
        <marker id={`${markerPre}-circle-start`} markerWidth="10" markerHeight="10" refX="0" refY="3" orient="auto" markerUnits="strokeWidth">
          <circle cx="3" cy="3" r="3" fill={col} />
        </marker>
        {/* ERD END markers */}
        <marker id={`${markerPre}-erd-inherit-end`} markerWidth="10" markerHeight="8" refX="9" refY="4" orient="auto" markerUnits="strokeWidth">
          <path d="M0,0 L0,8 L9,4 z" fill="none" stroke={col} strokeWidth="1" />
        </marker>
        <marker id={`${markerPre}-erd-assoc-end`} markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto" markerUnits="strokeWidth">
          <path d="M0,0 L7,3 L0,6" fill="none" stroke={col} strokeWidth="1.5" />
        </marker>
        <marker id={`${markerPre}-erd-comp-end`} markerWidth="12" markerHeight="8" refX="11" refY="4" orient="auto" markerUnits="strokeWidth">
          <path d="M0,4 L5,0 L11,4 L5,8 z" fill={col} stroke={col} strokeWidth="1" />
        </marker>
        <marker id={`${markerPre}-erd-agg-end`} markerWidth="12" markerHeight="8" refX="11" refY="4" orient="auto" markerUnits="strokeWidth">
          <path d="M0,4 L5,0 L11,4 L5,8 z" fill="white" stroke={col} strokeWidth="1" />
        </marker>
        {/* ERD START markers */}
        <marker id={`${markerPre}-erd-inherit-start`} markerWidth="10" markerHeight="8" refX="0" refY="4" orient="auto" markerUnits="strokeWidth">
          <path d="M9,0 L9,8 L0,4 z" fill="none" stroke={col} strokeWidth="1" />
        </marker>
        <marker id={`${markerPre}-erd-assoc-start`} markerWidth="8" markerHeight="6" refX="0" refY="3" orient="auto" markerUnits="strokeWidth">
          <path d="M7,0 L0,3 L7,6" fill="none" stroke={col} strokeWidth="1.5" />
        </marker>
        <marker id={`${markerPre}-erd-comp-start`} markerWidth="12" markerHeight="8" refX="0" refY="4" orient="auto" markerUnits="strokeWidth">
          <path d="M11,4 L6,0 L0,4 L6,8 z" fill={col} stroke={col} strokeWidth="1" />
        </marker>
        <marker id={`${markerPre}-erd-agg-start`} markerWidth="12" markerHeight="8" refX="0" refY="4" orient="auto" markerUnits="strokeWidth">
          <path d="M11,4 L6,0 L0,4 L6,8 z" fill="white" stroke={col} strokeWidth="1" />
        </marker>
      </defs>

      {/* Hit area — thick transparent stroke for easier clicking */}
      <path
        d={pathStr}
        fill="none"
        stroke="transparent"
        strokeWidth={Math.max(12, element.strokeWidth || 2)}
        style={{ cursor: 'move', pointerEvents: 'stroke' }}
        onMouseDown={onMouseDown}
        onDoubleClick={(e) => { e.preventDefault(); e.stopPropagation(); onContextMenu(e); }}
        onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); onContextMenu(e); }}
      />

      {/* Marker holder — invisible full-length path that carries the markers */}
      <path
        d={pathStr}
        fill="none"
        stroke={col}
        strokeWidth={element.strokeWidth}
        strokeOpacity="0"
        markerStart={getStartMarker(element.startCap)}
        markerEnd={getEndMarker(element.endCap)}
        style={{ pointerEvents: 'none' }}
      />

      {/* Visible line — shortened so it doesn't poke through marker tips */}
      <path
        d={visiblePathStr}
        fill="none"
        stroke={element.stroke}
        strokeWidth={element.strokeWidth}
        strokeDasharray={element.dashed ? '5,5' : 'none'}
        style={{ pointerEvents: 'none', filter: filterStyle }}
      />

      {/* Intermediate path-point drag handles (shown when selected, skips endpoints) */}
      {element.selected && pts.length > 2 && pts.slice(1, -1).map((p, i) => {
        const realIdx = i + 1;
        return (
          <circle
            key={`pt-${p.x.toFixed(1)}-${p.y.toFixed(1)}`}
            cx={p.x}
            cy={p.y}
            r={5 / z}
            fill="#007ACC"
            stroke="#fff"
            strokeWidth={2 / z}
            style={{ cursor: 'grab' }}
            onMouseDown={(e) => {
              e.stopPropagation();
              onSelect(element.id);
              const svgEl = (e.target as SVGElement).closest('svg') as SVGSVGElement | null;
              if (!svgEl) return;
              const move = (ev: MouseEvent) => {
                const { x, y } = getMouseWorldFromSvg(ev, svgEl, z);
                onArrowPathPointDrag?.(element.id, realIdx, x, y);
              };
              const up = () => {
                document.removeEventListener('mousemove', move);
                document.removeEventListener('mouseup', up);
              };
              document.addEventListener('mousemove', move);
              document.addEventListener('mouseup', up);
            }}
          />
        );
      })}

      {/* Midpoint insert handles: click to insert a new bend point */}
      {element.selected && pts.length >= 2 && pts.slice(0, -1).map((p, i) => {
        const next = pts[i + 1];
        const midX = (p.x + next.x) / 2;
        const midY = (p.y + next.y) / 2;
        return (
          <circle
            key={`mid-${i}-${midX.toFixed(1)}`}
            cx={midX}
            cy={midY}
            r={4 / z}
            fill="rgba(0, 122, 204, 0.35)"
            stroke="#007ACC"
            strokeWidth={1.5 / z}
            style={{ cursor: 'crosshair' }}
            onMouseDown={(e) => {
              e.stopPropagation();
              onSelect(element.id);
              const svgEl = (e.target as SVGElement).closest('svg') as SVGSVGElement | null;
              if (!svgEl) return;
              onArrowAddPoint?.(element.id, midX, midY, i);
              const newIdx = i + 1;
              const move = (ev: MouseEvent) => {
                const { x, y } = getMouseWorldFromSvg(ev, svgEl, z);
                onArrowPathPointDrag?.(element.id, newIdx, x, y);
              };
              const up = () => {
                document.removeEventListener('mousemove', move);
                document.removeEventListener('mouseup', up);
              };
              document.addEventListener('mousemove', move);
              document.addEventListener('mouseup', up);
            }}
          />
        );
      })}

      {/* Endpoint grab handles — visible blue circles when selected */}
      <circle
        cx={pts[0].x}
        cy={pts[0].y}
        r={element.selected ? 5 / z : 6 / z}
        fill={element.selected ? '#007ACC' : 'transparent'}
        stroke={element.selected ? '#fff' : 'transparent'}
        strokeWidth={element.selected ? 2 / z : 0}
        style={{ cursor: 'grab' }}
        onMouseDown={(e) => handleArrowPointMouseDown(e, true)}
      />
      <circle
        cx={pts[pts.length - 1].x}
        cy={pts[pts.length - 1].y}
        r={element.selected ? 5 / z : 6 / z}
        fill={element.selected ? '#007ACC' : 'transparent'}
        stroke={element.selected ? '#fff' : 'transparent'}
        strokeWidth={element.selected ? 2 / z : 0}
        style={{ cursor: 'grab' }}
        onMouseDown={(e) => handleArrowPointMouseDown(e, false)}
      />
    </g>
  );
};
